import React, { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, RotateCcw, X } from 'lucide-react'
import PropertyConditionOptions from '../../components/PropertyConditionOptions'
import { getGalleryImageUrl } from '../../helpers/galleryHelpers'
import { formatComparableDaysOnMarket, getComparableMarketStatus } from '../../helpers/comparableMarketStatus'

const formatPrice = (value) => (Number.isFinite(Number(value)) ? Math.round(Number(value)).toLocaleString('cs-CZ') : '—')
const formatFactor = (factor) => {
  if (!Number.isFinite(Number(factor))) return '—'
  const percentage = (Number(factor) - 1) * 100
  if (Math.abs(percentage) < 0.05) return '0 %'
  return `${percentage > 0 ? '+' : ''}${percentage.toFixed(1)} %`
}

const normalizeFloor = (value) => {
  if (value === null || value === undefined || value === '') return null
  return value
}

const ComparablePropertyModal = ({ property, editable = false, allowValuationOnly = true, showComparisonIgnore = false, saving = false, onClose, onSaveOverride, onSaveProperty }) => {
  const [imageIndex, setImageIndex] = useState(0)
  const [form, setForm] = useState({ m2: '', condition: '', floor: '', hasElevator: '', underRoof: false, ignoreForComparisons: false })
  const [onlyThisValuation, setOnlyThisValuation] = useState(false)

  const sourceValues = useMemo(
    () =>
      property?.sourceValues || {
        m2: property?.m2,
        condition: property?.condition ?? null,
        floor: property?.floor ?? null,
        hasElevator: property?.hasElevator ?? null,
        underRoof: Boolean(property?.underRoof),
      },
    [property]
  )
  const effectiveValues = property?.effectiveValues || sourceValues
  const gallery = property?.gallery?.length ? property.gallery : property?.imageUrl ? [property.imageUrl] : []
  const marketStatus = property ? getComparableMarketStatus(property) : null
  const marketSpeedBandLabel = property?.marketSpeedBandLabel?.replace(/^Floor:/, 'Spodní hranice:')

  useEffect(() => {
    if (!property) return
    setImageIndex(0)
    setOnlyThisValuation(false)
    setForm({
      m2: effectiveValues.m2 ?? '',
      condition: effectiveValues.condition ?? '',
      floor: effectiveValues.floor ?? '',
      hasElevator:
        effectiveValues.hasElevator === true ? 'true' : effectiveValues.hasElevator === false ? 'false' : '',
      underRoof: Boolean(effectiveValues.underRoof),
      ignoreForComparisons: Boolean(property.ignoreForComparisons),
    })
  }, [property, effectiveValues.m2, effectiveValues.condition, effectiveValues.floor, effectiveValues.hasElevator, effectiveValues.underRoof])

  useEffect(() => {
    if (!property) return undefined
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !saving) onClose()
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [property, saving, onClose])

  if (!property) return null

  const handleSave = () => {
    const m2 = Number(form.m2)
    if (!(m2 > 0)) return

    const nextValues = {
      m2,
      condition: form.condition,
      floor: normalizeFloor(form.floor),
      hasElevator: form.hasElevator === '' ? null : form.hasElevator === 'true',
      underRoof: form.underRoof,
      ...(showComparisonIgnore && { ignoreForComparisons: form.ignoreForComparisons }),
    }
    if (allowValuationOnly && onlyThisValuation) {
      const override = {}
      if (nextValues.m2 !== Number(sourceValues.m2)) override.m2 = nextValues.m2
      if (nextValues.condition !== sourceValues.condition) override.condition = nextValues.condition
      if (nextValues.floor !== normalizeFloor(sourceValues.floor)) override.floor = nextValues.floor
      if (nextValues.hasElevator !== sourceValues.hasElevator) override.hasElevator = nextValues.hasElevator
      if (nextValues.underRoof !== Boolean(sourceValues.underRoof)) override.underRoof = nextValues.underRoof
      onSaveOverride(property.propertyId, Object.keys(override).length ? override : null)
      return
    }

    onSaveProperty(property.propertyId, nextValues)
  }

  const imageUrl = getGalleryImageUrl(gallery, imageIndex)

  return (
    <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/60 p-3 sm:p-6" role="dialog" aria-modal="true">
      <button type="button" className="absolute inset-0 cursor-default" aria-label="Zavřít" onClick={() => !saving && onClose()} />
      <div className="relative z-10 flex max-h-[94vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-4 py-3 sm:px-6">
          <div>
            <h2 className="text-base font-semibold text-gray-900 sm:text-lg">{property.title || 'Srovnatelná nabídka'}</h2>
            <p className="mt-1 text-xs text-gray-500">{property.location}</p>
          </div>
          <button type="button" disabled={saving} onClick={onClose} className="rounded-md p-2 text-gray-500 hover:bg-gray-100 disabled:text-gray-300">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="overflow-y-auto">
          <div className="grid grid-cols-1 gap-0 lg:grid-cols-2">
            <div className="self-start bg-white p-3 sm:p-5">
              <div className="relative flex aspect-[4/3] items-center justify-center overflow-hidden rounded-lg bg-gray-100">
                {imageUrl ? (
                  <img src={imageUrl} alt="" className="h-full w-full object-contain" />
                ) : (
                  <span className="text-sm text-gray-400">Bez fotografie</span>
                )}
                {gallery.length > 1 && (
                  <>
                    <button
                      type="button"
                      onClick={() => setImageIndex((current) => (current - 1 + gallery.length) % gallery.length)}
                      className="absolute left-2 rounded-full bg-black/60 p-2 text-white hover:bg-black/80"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => setImageIndex((current) => (current + 1) % gallery.length)}
                      className="absolute right-2 rounded-full bg-black/60 p-2 text-white hover:bg-black/80"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                    <span className="absolute bottom-2 right-2 rounded bg-black/60 px-2 py-1 text-[10px] text-white">
                      {imageIndex + 1} / {gallery.length}
                    </span>
                  </>
                )}
              </div>
              {gallery.length > 1 && (
                <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
                  {gallery.slice(0, 20).map((image, index) => (
                    <button
                      key={`${getGalleryImageUrl(gallery, index)}:${index}`}
                      type="button"
                      onClick={() => setImageIndex(index)}
                      className={`h-14 w-20 shrink-0 overflow-hidden rounded border-2 ${
                        imageIndex === index ? 'border-blue-500' : 'border-transparent'
                      }`}
                    >
                      <img src={getGalleryImageUrl([image])} alt="" className="h-full w-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-5 p-4 sm:p-6">
              <div className="grid grid-cols-2 gap-3 text-xs">
                <div>
                  <div className="text-gray-500">Nabídková cena</div>
                  <div className="mt-1 font-semibold text-gray-900">{formatPrice(property.price)} Kč</div>
                </div>
                <div>
                  <div className="text-gray-500">Původní cena/m²</div>
                  <div className="mt-1 font-semibold text-gray-900">{formatPrice(property.sourceM2Price ?? property.m2Price)} Kč/m²</div>
                </div>
                <div>
                  <div className="text-gray-500">Po úpravách</div>
                  <div className="mt-1 font-semibold text-gray-900">{formatPrice(property.adjustedM2Price)} Kč/m²</div>
                </div>
                <div>
                  <div className="text-gray-500">Stav nabídky</div>
                  <div className={`mt-1 inline-flex rounded-full px-2 py-1 font-medium ${marketStatus.badgeClassName}`}>
                    {marketStatus.label} · {formatComparableDaysOnMarket(marketStatus.daysOnMarket)}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 rounded-lg border border-gray-200 p-3 text-xs sm:grid-cols-3">
                <div>
                  <div className="text-gray-500">Výměra</div>
                  <div className="mt-1 font-medium text-gray-900">{effectiveValues.m2 ?? '—'} m²</div>
                </div>
                <div>
                  <div className="text-gray-500">Dispozice</div>
                  <div className="mt-1 font-medium text-gray-900">{property.layout || '—'}</div>
                </div>
                <div>
                  <div className="text-gray-500">Stav</div>
                  <div className="mt-1 font-medium text-gray-900">{effectiveValues.condition || '—'}</div>
                </div>
                <div>
                  <div className="text-gray-500">Podlaží</div>
                  <div className="mt-1 font-medium text-gray-900">{effectiveValues.floor ?? '—'}</div>
                </div>
                <div>
                  <div className="text-gray-500">Výtah</div>
                  <div className="mt-1 font-medium text-gray-900">
                    {effectiveValues.hasElevator === true ? 'Ano' : effectiveValues.hasElevator === false ? 'Ne' : 'Neuvedeno'}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Pod střechou</div>
                  <div className="mt-1 font-medium text-gray-900">{effectiveValues.underRoof ? 'Ano' : 'Ne'}</div>
                </div>
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-xs text-gray-700">
                <div className="font-semibold text-gray-900">Úpravy ceny</div>
                <div className="mt-2 grid grid-cols-1 gap-1 sm:grid-cols-3">
                  <span>Rychlost: {formatFactor(property.marketSpeedAdjustmentFactor)}</span>
                  <span>Výměra: {formatFactor(property.areaAdjustmentFactor)}</span>
                  <span>Podlaží: {formatFactor(property.floorAdjustmentFactor)}</span>
                </div>
                {marketSpeedBandLabel && <div className="mt-2 text-gray-500">{marketSpeedBandLabel}</div>}
              </div>

              {Number.isFinite(property.arithmeticRelevanceWeight) && (
                <div className="rounded-lg border border-gray-200 p-3 text-xs text-gray-700">
                  <div className="font-semibold text-gray-900">Relevance</div>
                  <div className="mt-2 grid grid-cols-1 gap-1 sm:grid-cols-3">
                    <span>Průměr: {Math.round(property.arithmeticRelevanceWeight * 100)} %</span>
                    <span>
                      Součin:{' '}
                      {Number.isFinite(property.multiplicativeRelevanceWeight)
                        ? `${Math.round(property.multiplicativeRelevanceWeight * 100)} %`
                        : '—'}
                    </span>
                    <span>Použito: {Number.isFinite(property.relevanceWeight) ? `${Math.round(property.relevanceWeight * 100)} %` : '—'}</span>
                  </div>
                </div>
              )}

              {property.description && (
                <div>
                  <h3 className="text-xs font-semibold text-gray-900">Popis nabídky</h3>
                  <p className="mt-2 max-h-32 overflow-y-auto whitespace-pre-line text-xs leading-relaxed text-gray-600">{property.description}</p>
                </div>
              )}

              {editable ? (
                <div>
                  <div className="mb-3 flex items-center justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">Upravit údaje nabídky</h3>
                      <p className="mt-1 text-[10px] text-gray-500">
                        {allowValuationOnly && onlyThisValuation
                          ? 'Změna se použije pouze v tomto odhadu.'
                          : 'Změna se uloží do nemovitosti, zapíše do historie a použije i v dalších odhadech.'}
                      </p>
                    </div>
                    {property.hasComparableOverride && (
                      <button
                        type="button"
                        disabled={saving}
                        onClick={() => onSaveOverride(property.propertyId, null)}
                        className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900 disabled:text-gray-300"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        Obnovit
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label className="text-xs text-gray-700">
                      Výměra
                      <input
                        type="number"
                        min="1"
                        max="2000"
                        step="0.1"
                        value={form.m2}
                        onChange={(event) => setForm((current) => ({ ...current, m2: event.target.value }))}
                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
                      />
                      <span className="mt-1 block text-[10px] text-gray-400">Zdroj: {sourceValues.m2 ?? '—'} m²</span>
                    </label>
                    <label className="text-xs text-gray-700">
                      Stav
                      <select
                        value={form.condition}
                        onChange={(event) => setForm((current) => ({ ...current, condition: event.target.value }))}
                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
                      >
                        <PropertyConditionOptions />
                      </select>
                      <span className="mt-1 block text-[10px] text-gray-400">Zdroj: {sourceValues.condition || '—'}</span>
                    </label>
                    <label className="text-xs text-gray-700">
                      Podlaží
                      <input
                        type="text"
                        value={form.floor}
                        onChange={(event) => setForm((current) => ({ ...current, floor: event.target.value }))}
                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
                        placeholder="např. 4. patro"
                      />
                      <span className="mt-1 block text-[10px] text-gray-400">Zdroj: {sourceValues.floor ?? '—'}</span>
                    </label>
                    <label className="text-xs text-gray-700">
                      Výtah
                      <select
                        value={form.hasElevator}
                        onChange={(event) => setForm((current) => ({ ...current, hasElevator: event.target.value }))}
                        className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2"
                      >
                        <option value="">Neuvedeno</option>
                        <option value="true">Ano</option>
                        <option value="false">Ne</option>
                      </select>
                    </label>
                    <label className="flex items-center gap-2 text-xs text-gray-700 sm:col-span-2">
                      <input
                        type="checkbox"
                        checked={form.underRoof}
                        onChange={(event) => setForm((current) => ({ ...current, underRoof: event.target.checked }))}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600"
                      />
                      Přímo pod střechou
                    </label>
                    {showComparisonIgnore && (
                      <label className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 sm:col-span-2">
                        <input
                          type="checkbox"
                          checked={form.ignoreForComparisons}
                          onChange={(event) => setForm((current) => ({ ...current, ignoreForComparisons: event.target.checked }))}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-amber-600"
                        />
                        <span>
                          <span className="block font-medium">Ignorovat ve srovnáních</span>
                          <span className="mt-1 block text-[10px] text-amber-800">Nabídka se nebude používat jako srovnatelná nemovitost v nových ani přepočítaných odhadech.</span>
                        </span>
                      </label>
                    )}
                    {allowValuationOnly && (
                      <label className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900 sm:col-span-2">
                        <input
                          type="checkbox"
                          checked={onlyThisValuation}
                          onChange={(event) => setOnlyThisValuation(event.target.checked)}
                          className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600"
                        />
                        <span>
                          <span className="block font-medium">Upravit pouze pro tento odhad</span>
                          <span className="mt-1 block text-[10px] text-blue-700">Původní nemovitost zůstane beze změny.</span>
                        </span>
                      </label>
                    )}
                  </div>
                  <div className="mt-5 flex justify-end gap-2">
                    <button type="button" disabled={saving} onClick={onClose} className="rounded-md border border-gray-300 px-4 py-2 text-xs text-gray-700">
                      Zrušit
                    </button>
                    <button
                      type="button"
                      disabled={saving || !(Number(form.m2) > 0) || !form.condition}
                      onClick={handleSave}
                      className="rounded-md bg-blue-600 px-4 py-2 text-xs font-medium text-white hover:bg-blue-700 disabled:bg-gray-400"
                    >
                      {saving ? 'Ukládám a přepočítávám…' : allowValuationOnly && onlyThisValuation ? 'Použít v tomto odhadu' : 'Uložit nemovitost a přepočítat'}
                    </button>
                  </div>
                </div>
              ) : (
                property.hasComparableOverride && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900">
                    <div className="font-medium">Tato nabídka používá hodnoty upravené pouze pro tento odhad.</div>
                    <div className="mt-1 text-[10px] text-blue-700">
                      Zdroj: {sourceValues.m2 ?? '—'} m² · {sourceValues.condition || '—'} · {sourceValues.floor ?? 'podlaží neuvedeno'}
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ComparablePropertyModal
